import type { ExampleOverride } from './module-example';
import { isObject, type JsonObject } from './openapi-utils';

/** Fractions are worked out in whole hundredths, so each prints as a short decimal like 0.33 */
const HUNDREDTHS = 100;

/** Reads the object at a dotted path such as "solidStorage.manureAllocated" */
const getAt = (value: unknown, path: readonly string[]): unknown =>
  path.reduce<unknown>((node, name) => (isObject(node) ? node[name] : undefined), value);

/** Copies the objects along a path, merging the given fields into the object at the end of it */
const mergeAt = (value: JsonObject, path: readonly string[], fields: JsonObject): JsonObject => {
  const [name, ...rest] = path;
  const child = value[name] as JsonObject;
  return { ...value, [name]: rest.length === 0 ? { ...child, ...fields } : mergeAt(child, rest, fields) };
};

/**
 * Shares 1 across several objects, setting the given field on each, e.g.
 * { a: { value: 0.33 }, b: { value: 0.33 }, c: { value: 0.34 } }. The last object takes the remainder.
 *
 * By default the objects are every child of the value. Pass `across` to name them by dotted paths instead,
 * for groups spread over different depths, e.g. ['solidStorage.manureAllocated', 'outdoorAndFreeRange'].
 * Paths missing from the value are skipped, so one rule can list the members of several similar shapes.
 *
 * The API adds these up as decimals, not floats, so the printed values must total exactly 1. Doing the
 * arithmetic on integers and dividing once per value avoids float noise like 1 - 0.2 * 4 = 0.19999999999999996.
 */
export const splitEvenly =
  (field: string, across?: readonly string[]) =>
  (value: unknown): unknown => {
    if (!isObject(value)) return value;
    const paths = (across?.map((path) => path.split('.')) ?? Object.keys(value).map((name) => [name])).filter((path) =>
      isObject(getAt(value, path)),
    );
    if (paths.length === 0) return value;
    const share = Math.floor(HUNDREDTHS / paths.length);
    const remainder = HUNDREDTHS - share * (paths.length - 1);
    return paths.reduce((result, path, i) => {
      const units = i === paths.length - 1 ? remainder : share;
      return mergeAt(result, path, { [field]: units / HUNDREDTHS });
    }, value);
  };

/** Rules the runtime validation enforces beyond the schema, for each calculator version */
const OVERRIDES_BY_VERSION: Record<string, readonly ExampleOverride[]> = {
  '4.0.2': [
    // Stage 2 treatment fractions for each manure management system must add up to 1
    { path: ['treatmentStage2Allocations'], apply: splitEvenly('value') },
    // Fractions sent to each primary manure management system must add up to 1
    {
      path: ['manureAllocations'],
      apply: splitEvenly('value', [
        // Pigs
        'anaerobicLagoon.manureAllocated',
        'solidStorage.manureAllocated',
        'digester.manureAllocated',
        'pitStorage.manureAllocated',
        'deepLitter.manureAllocated',
        'outdoorAndFreeRange',
        // Poultry
        'manureWithLitter.manureAllocated',
        'beltManureRemoval.manureAllocated',
        'manureStoredInHouse.manureAllocated',
        'pastureRangeAndPaddock',
      ]),
    },
    // Feedlot groups transfer manure out of the drylot, and those fractions must add up to 1. Matched on the
    // parent too, as pigs also have a solidStorage under manureAllocations, nested one level deeper.
    {
      path: ['groups', 'manureAllocations'],
      apply: splitEvenly('value', ['solidStorage', 'composting', 'directApplication']),
    },
  ],
};

/** Looks up the overrides by the version the spec declares */
export const getExampleOverrides = (spec: JsonObject): readonly ExampleOverride[] => {
  const version = isObject(spec.info) ? spec.info.version : undefined;
  return (typeof version === 'string' && OVERRIDES_BY_VERSION[version]) || [];
};
